import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState("");
  const navigate = useNavigate();

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const requestOtp = async () => {
    if (!email) return toast.error("Enter your email");
    setLoading(true);
    const res = await fetch(`${API}/api/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    const data = await res.json();
    setLoading(false);
    if (res.ok) {
      toast.success(data.message);
      setStep(2);
    } else {
      toast.error(data.message);
    }
  };

  const resetPassword = async () => {
    const otpString = otp.join("");
    if (otpString.length < 6 || !newPassword)
      return toast.error("Enter OTP and new password");
    setLoading(true);
    const res = await fetch(`${API}/api/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp: otpString, newPassword }),
    });
    const data = await res.json();
    setLoading(false);
    if (res.ok) {
      toast.success(data.message);
      setTimeout(() => navigate("/login"), 2000);
    } else {
      toast.error(data.message);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,600&family=DM+Sans:wght@300;400;500&display=swap');
        .font-cormorant { font-family: 'Cormorant Garamond', serif; }
        .font-dm { font-family: 'DM Sans', sans-serif; }

        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner {
          display: inline-block; width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4); border-top-color: #fff;
          border-radius: 50%; animation: spin 0.7s linear infinite;
          vertical-align: middle; margin-right: 8px;
        }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeSlideUp 0.45s ease both; }

        .auth-input:focus { box-shadow: 0 0 0 3px rgba(26,111,168,0.1); }

        .otp-input {
          width: 44px; height: 52px;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          text-align: center;
          font-size: 20px;
          font-weight: 600;
          color: #0a3d62;
          background: #fff;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          font-family: 'DM Sans', sans-serif;
        }
        .otp-input:focus {
          border-color: #1a6fa8;
          box-shadow: 0 0 0 3px rgba(26,111,168,0.12);
        }
        .otp-input:not(:placeholder-shown) {
          border-color: #1a6fa8;
          background: #f0f7ff;
        }

        .step-dot {
          width: 8px; height: 8px; border-radius: 50%;
          transition: all 0.3s ease;
        }
      `}</style>

      <div className="font-dm min-h-screen flex">

        {/* ── Left Panel (identical to Login) ── */}
        <div
          className="hidden md:flex w-[45%] flex-col justify-between p-12 relative overflow-hidden"
          style={{ background: "linear-gradient(145deg,#0a3d62 0%,#1a6fa8 50%,#0d8f8f 100%)" }}
        >
          <div className="absolute w-[500px] h-[500px] rounded-full border border-white/[0.08] -top-36 -left-36 pointer-events-none" />
          <div className="absolute w-[300px] h-[300px] rounded-full border border-white/[0.06] bottom-20 -right-20 pointer-events-none" />

          {/* Brand */}
          <div className="flex items-center gap-3 z-10">
            <div className="w-11 h-11 bg-white/15 rounded-xl flex items-center justify-center text-2xl">🏥</div>
            <span className="font-cormorant text-[22px] font-semibold text-white tracking-wide">MediBook</span>
          </div>

          {/* Headline */}
          <div className="z-10">
            <h1 className="font-cormorant text-[46px] font-light text-white leading-[1.2] mb-5">
              Reset your<br />
              <span className="font-semibold italic">password,</span><br />
              securely.
            </h1>
            <p className="text-white/65 text-[15px] leading-relaxed font-light max-w-xs">
              We'll send a one-time code to your email. It only takes a moment to get back in.
            </p>

            {/* Steps indicator */}
            <div className="mt-12 flex flex-col gap-4">
              {[
                { n: 1, label: "Enter your email address" },
                { n: 2, label: "Verify with OTP" },
                { n: 3, label: "Set new password" },
              ].map(({ n, label }) => {
                const done = step > n;
                const active = step === n;
                return (
                  <div key={n} className="flex items-center gap-3">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300"
                      style={{
                        background: done ? "#fff" : active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.15)",
                        color: done || active ? "#0a3d62" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      {done ? "✓" : n}
                    </div>
                    <span className={`text-sm transition-all duration-300 ${active ? "text-white" : done ? "text-white/60 line-through" : "text-white/35"}`}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Decorative cross */}
          <svg className="absolute bottom-40 right-12 z-10 opacity-[0.12]" width="120" height="120" viewBox="0 0 120 120" fill="none">
            <rect x="45" y="0" width="30" height="120" rx="15" fill="white" />
            <rect x="0" y="45" width="120" height="30" rx="15" fill="white" />
          </svg>

          <p className="z-10 text-white/35 text-xs tracking-wide">© 2025 MediBook · All rights reserved</p>
        </div>

        {/* ── Right Panel ── */}
        <div className="flex-1 flex items-center justify-center px-8 py-12 bg-slate-50">
          <div className="w-full max-w-sm">

            {/* ── STEP 1: Email ── */}
            {step === 1 && (
              <div className="fade-up">
                {/* Back to login */}
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:text-[#1a6fa8] transition-colors mb-8"
                >
                  ← Back to login
                </Link>

                <h2 className="font-cormorant text-[36px] font-medium text-[#0a3d62] mb-1">
                  Forgot password?
                </h2>
                <p className="text-slate-400 text-sm font-light mb-9">
                  No worries — enter your email and we'll send an OTP.
                </p>

                {/* Email input */}
                <div className="mb-7">
                  <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none transition-colors duration-200 ${focused === "email" ? "text-[#1a6fa8]" : "text-slate-300"}`}>
                      ✉
                    </span>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setFocused("email")}
                      onBlur={() => setFocused("")}
                      onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                      className="auth-input font-dm w-full pl-10 pr-4 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#1a6fa8]"
                    />
                  </div>
                </div>

                <button
                  onClick={requestOtp}
                  disabled={loading}
                  className="font-dm w-full py-3.5 rounded-xl text-white text-[15px] font-medium tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:!transform-none"
                  style={{ background: "linear-gradient(135deg,#0a3d62,#1a6fa8)" }}
                >
                  {loading && <span className="spinner" />}
                  {loading ? "Sending OTP..." : "Send OTP"}
                </button>

                <p className="text-center text-sm text-slate-400 mt-6">
                  Remembered it?{" "}
                  <Link to="/login" className="text-[#1a6fa8] font-medium hover:underline">Sign in</Link>
                </p>
              </div>
            )}

            {/* ── STEP 2: OTP + New Password ── */}
            {step === 2 && (
              <div className="fade-up">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 text-[13px] text-slate-400 hover:text-[#1a6fa8] transition-colors mb-8"
                >
                  ← Back
                </button>

                <h2 className="font-cormorant text-[36px] font-medium text-[#0a3d62] mb-1">
                  Check your email
                </h2>
                <p className="text-slate-400 text-sm font-light mb-1">
                  We sent a 6-digit code to
                </p>
                <p className="text-[#1a6fa8] text-sm font-medium mb-9">{email}</p>

                {/* OTP boxes */}
                <div className="mb-6">
                  <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-3">
                    One-Time Password
                  </label>
                  <div className="flex gap-2 justify-between">
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        id={`otp-${i}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        placeholder="·"
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        className="otp-input"
                      />
                    ))}
                  </div>
                </div>

                {/* New Password */}
                <div className="mb-7">
                  <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <span className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-base pointer-events-none transition-colors duration-200 ${focused === "password" ? "text-[#1a6fa8]" : "text-slate-300"}`}>
                      🔒
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onFocus={() => setFocused("password")}
                      onBlur={() => setFocused("")}
                      className="auth-input font-dm w-full pl-10 pr-10 py-3 border-[1.5px] border-slate-200 rounded-xl text-sm bg-white text-slate-800 placeholder-slate-300 outline-none transition-all duration-200 focus:border-[#1a6fa8]"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 cursor-pointer text-lg text-slate-400 hover:text-[#1a6fa8]"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>

                <button
                  onClick={resetPassword}
                  disabled={loading}
                  className="font-dm w-full py-3.5 rounded-xl text-white text-[15px] font-medium tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:opacity-90 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:!transform-none"
                  style={{ background: "linear-gradient(135deg,#0a3d62,#1a6fa8)" }}
                >
                  {loading && <span className="spinner" />}
                  {loading ? "Resetting..." : "Reset Password"}
                </button>

                {/* Resend */}
                <p className="text-center text-sm text-slate-400 mt-6">
                  Didn't receive it?{" "}
                  <button
                    onClick={requestOtp}
                    className="text-[#1a6fa8] font-medium hover:underline bg-transparent border-none cursor-pointer p-0"
                  >
                    Resend OTP
                  </button>
                </p>
              </div>
            )}

            {/* Trust badges */}
            <div className="flex justify-center gap-5 mt-10">
              {["🔒 Secure", "🛡️ HIPAA", "✅ Verified Doctors"].map((item) => (
                <div key={item} className="text-[11px] text-slate-300">{item}</div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}